import React, { useEffect, useState } from "react";
import { MdAdd, MdCancel, MdDelete, MdEdit, MdSave } from "react-icons/md";
import AgendaNavbar from "../components/AgendaNavbar";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import {
  createOsservazione,
  fetchOsservazione,
} from "../redux/slices/agendaSlice";
import Loading from "../components/utils/Loading";
import { useNavigate, useParams } from "react-router-dom";
import IOsservazione, { exampleOsservazione } from "../types/IOsservazione";
import { fetchAnagraficaById } from "../redux/slices/anagraficaSlice";
import { addNotification } from "../redux/slices/notificationSlice";
import { ActivityOpinion, MessageType, UscitaType } from "../types";
import { exampleScheda } from "../types/IScheda";

function Osservazione() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { anno } = useParams<{ anno: string }>();
  const { num } = useParams<{ num: string }>();
  const dispatch = useAppDispatch();
  const { selectedOsservazione, loading, error } = useAppSelector(
    (state) => state.agenda
  );
  const {
    selectedAnagrafica,
    loading: loadingAnagrafica,
    error: errorAnagrafica,
  } = useAppSelector((state) => state.anagrafica);

  const initialFormData: IOsservazione = {
    ...exampleOsservazione,
    Id_Anagrafica: Number(id),
    Anno: anno ?? "",
    Num: Number(num),
  };

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<IOsservazione>(initialFormData);

  useEffect(() => {
    setFormData(initialFormData);
    if (id && anno && num) {
      dispatch(fetchOsservazione({ id: Number(id), anno, num: Number(num) }));
    }
  }, [id, anno, num, dispatch]);

  useEffect(() => {
    if (id) {
      dispatch(fetchAnagraficaById(Number(id)));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (selectedOsservazione && !error) {
      if (
        !selectedOsservazione.Schede ||
        selectedOsservazione.Schede.length === 0
      ) {
        setFormData({
          ...initialFormData,
          Schede: selectedOsservazione.Attivita.map((attivita) => ({
            ...exampleScheda,
            Id_Attivita: attivita.Id,
            Attivita: attivita,
          })),
          Attivita: selectedOsservazione.Attivita,
        });
        setIsEditing(true);
      } else {
        setFormData(selectedOsservazione);
        setIsEditing(false);
      }
    }
  }, [selectedOsservazione, error]);

  useEffect(() => {
    if (error) {
      if (!error.includes("non trovata")) {
        dispatch(addNotification({ message: error, type: MessageType.ERROR }));
      } else {
        if (selectedAnagrafica?.IsArchiviato) {
          dispatch(
            addNotification({
              message:
                "Anagrafica archiviata, non è possibile creare una nuova osservazione",
              type: MessageType.WARNING,
            })
          );
        }
        setFormData(initialFormData);
        setIsEditing(true);
      }
    } else {
      setIsEditing(false);
    }
  }, [error]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    // Estrai l'indice dallo schema del name (Es: Testo_0, Strategia_1)
    const match = name.match(/^(.+)_([0-9]+)$/);
    if (!match) return;

    const fieldName = match[1]; // Nome del campo (es. "Testo", "Strategia")
    const index = parseInt(match[2]); // Indice della scheda

    setFormData((prevFormData) => {
      const newSchede = [...prevFormData.Schede];

      // Aggiorna dinamicamente il campo della scheda corrispondente
      newSchede[index] = { ...newSchede[index], [fieldName]: value };

      return { ...prevFormData, Schede: newSchede };
    });
  };

  const handleChangeOpinion = (value: ActivityOpinion, index: number) => {
    setFormData((prevFormData) => {
      const newSchede = [...prevFormData.Schede];
      newSchede[index] = { ...newSchede[index], Giudizio: value };
      return { ...prevFormData, Schede: newSchede };
    });
  };

  const handleSave = () => {
    dispatch(createOsservazione(formData))
      .then((result) => {
        if (createOsservazione.fulfilled.match(result)) {
          setIsEditing(false);
          dispatch(
            addNotification({
              message: "Osservazione salvata",
              type: MessageType.SUCCESS,
              tag: "createOsservazione",
            })
          );
        } else {
          setIsEditing(true);
        }
      })
      .catch(() =>
        dispatch(
          addNotification({
            message: "Errore durante la creazione",
            type: MessageType.ERROR,
            tag: "createOsservazione",
          })
        )
      );
  };

  const handleAnnulla = () => {
    if (selectedOsservazione === null || !selectedOsservazione.Id) {
      navigate(`/storico/${id}`);
    } else {
      setFormData(selectedOsservazione || formData);
      setIsEditing(false);
    }
  };

  return (
    <div className="container">
      {loading && <Loading />}

      {!loading && (selectedOsservazione || isEditing) && (
        <>
          <AgendaNavbar
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            handleSave={handleSave}
            handleAnnulla={handleAnnulla}
          />

          <section
            className={`bg-white border border-gray-300 rounded-2xl rounded-tl-none shadow-md p-6 pb-8 relative z-[99]`}
          >
            <h2 className="h2">
              Anno Scolastico {anno} #{num?.toString().padStart(2, "0")}
            </h2>
          </section>

          {formData.Schede.map((scheda, index) => (
            <section
              key={index}
              id={`scheda_${index}`}
              className={`bg-white border border-gray-300 rounded-2xl -mt-2 shadow-md p-6 pb-8 relative z-[99]`}
            >
              <h2 className="h2">{scheda.Attivita.Nome}</h2>
              <div className="my-4 flex items-center justify-between">
                <h4 className="h4">Giudizio</h4>
                {Object.values(ActivityOpinion).map((opinion) => (
                  <div
                    key={opinion}
                    className={`form-element ${!isEditing ? "disabled" : ""}`}
                    onClick={() =>
                      isEditing && handleChangeOpinion(opinion, index)
                    }
                  >
                    <div className="radio-button">
                      {scheda.Giudizio === opinion && (
                        <span className="selected"></span>
                      )}
                    </div>
                    <label className="cursor-pointer">{opinion}</label>
                  </div>
                ))}
              </div>
              <p className="mt-4 mb-2">
                <b>NOTE:</b> {scheda.Attivita.Note}
              </p>
              <div className="form-element">
                <textarea
                  name={`Testo_${index}`}
                  className="w-full"
                  rows={10}
                  value={scheda.Testo ?? ""}
                  disabled={!isEditing}
                  onChange={handleChange}
                ></textarea>
              </div>
              <p className="mt-4 mb-2">
                Quali strategie gli Educatori hanno messo in atto per superare
                eventuali difficoltà
              </p>
              <div className="form-element">
                <textarea
                  name={`Strategia_${index}`}
                  className="w-full"
                  rows={3}
                  value={scheda.Strategia ?? ""}
                  disabled={!isEditing}
                  onChange={handleChange}
                ></textarea>
              </div>
            </section>
          ))}

          <div className="flex items-center justify-between mt-4">
            <button
              className="btn btn-lg"
              disabled={isEditing || selectedAnagrafica?.IsArchiviato}
              onClick={() => setIsEditing(!isEditing)}
            >
              Modifica <MdEdit size={20} />
            </button>

            <div className="flex items-center gap-4">
              <button
                className="btn btn-lg"
                disabled={!isEditing || selectedAnagrafica?.IsArchiviato}
                onClick={handleSave}
              >
                Salva <MdSave size={20} />
              </button>
              <button
                className="btn btn-lg"
                disabled={!isEditing || selectedAnagrafica?.IsArchiviato}
                onClick={handleAnnulla}
              >
                Annulla <MdCancel size={20} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Osservazione;
